import { useMutation } from "@tanstack/react-query";
import { useDispatch } from 'react-redux';

import { AppConfig } from "../config/app.config";

import { api } from "./ApiClient";

import { CookiesHelper } from "../helpers/cookies.helper";
import { AuthHelper } from "../helpers/auth.helper";

import { setUserData } from "../store/misc.slice";
import { closeModal } from "../store/modal.slice";

import { HMIResetPassword, HMIVerifyEmail, HMISendEmailVerificationEmail, HMISendPasswordResetEmail, HMISignIn, HMISignup, ISignInResponse, HMICreateGuestSession, HMISetPassword } from "../types/services.interfaces/auth-ux";

export const useCreateSession = () => {
    AuthHelper.presetSessionRequest(api);

    const mutationFn = async (): Promise<string> => {
        const res = await api.post('/auth-ux/create-session');
        return res.data;
    };

    return useMutation({
        mutationFn,
        onSuccess: (token) => {
            CookiesHelper.set(AppConfig.cookieTokenNames.session, token, (4 * 24 * 60 * 60 * 1000));
        },
    });
};

export const useCreateGuestSession = () => {
    AuthHelper.presetSessionRequest(api);

    const dispatch = useDispatch();

    const mutationFn = async (): Promise<string> => {
        const res = await api.get('/auth-ux/create-guest-session');
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables: HMICreateGuestSession) => {
            variables.setProcessing(true);
            variables.setError(null);
        },
        onSuccess: (token) => {
            CookiesHelper.set(AppConfig.cookieTokenNames.guest, token, (4 * 24 * 60 * 60 * 1000));
            CookiesHelper.remove(AppConfig.cookieTokenNames.session);

            dispatch(closeModal('AUTH_MODAL'));

            window.location.href = '/';
        },
        onError: (error: any, variables) => {
            variables.setError(error?.response?.data?.message ?? 'Something went wrong.');
        },
        onSettled: (_, __, variables) => {
            variables.setProcessing(false);
        },
    });
};

export const useSignup = () => {
    const dispatch = useDispatch();

    const mutationFn = async (data: HMISignup): Promise<ISignInResponse | null> => {
        AuthHelper.presetCustomRequest(api, data.token);

        const res = await api.post('/auth-ux/signup', data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setProcessing(true);
            variables.setError(null);
        },
        onSuccess: (data, variables) => {
            variables.setSuccess(true);

            if (variables.emailOrUsername === 'username') {
                data = data as ISignInResponse;
                CookiesHelper.set(AppConfig.cookieTokenNames.auth, data.auth_token, (4 * 24 * 60 * 60 * 1000));
                CookiesHelper.remove(AppConfig.cookieTokenNames.guest);
                CookiesHelper.remove(AppConfig.cookieTokenNames.session);
                CookiesHelper.remove(AppConfig.cookieTokenNames.mbtsConsent);
                dispatch(setUserData({ email: data.email, username: data.username }));

                setInterval(() => {
                    variables.setRedirectCountdown(prev => prev - 1);
                }, 1000);
            };
        },
        onError: (error: any, variables) => {
            variables.setError(error?.response?.data?.message ?? 'Something went wrong.');
        },
        onSettled: (_, __, variables) => {
            variables.setProcessing(false);
        },
    });
};

export const useSendEmailVerificationEmail = () => {
    const mutationFn = async (data: HMISendEmailVerificationEmail): Promise<string> => {
        AuthHelper.presetCustomRequest(api, data.token);

        const res = await api.patch('/auth-ux/send-email-verification', data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setProcessing(true);
            variables.setError(null);
        },
        onSuccess: (_, variables) => {
            variables.setSuccess(true);
        },
        onError: (error: any, variables) => {
            variables.setError(error?.response?.data?.message ?? 'Something went wrong.');
        },
        onSettled: (_, __, variables) => {
            variables.setProcessing(false);
        },
    });
};

export const useVerifyEmail = () => {
    const mutationFn = async (data: HMIVerifyEmail): Promise<string> => {
        AuthHelper.presetCustomRequest(api, data.token);

        const res = await api.patch('/auth-ux/verify-email');
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setProcessing(true);
            variables.setError(null);
        },
        onSuccess: (_, variables) => {
            variables.setSuccess(true);
        },
        onError: (error: any, variables) => {
            variables.setError(error?.response?.data?.message ?? 'Something went wrong.');
        },
        onSettled: (_, __, variables) => {
            variables.setProcessing(false);
        },
    });
};

export const useSendPasswordResetEmail = () => {
    const mutationFn = async (data: HMISendPasswordResetEmail): Promise<string> => {
        AuthHelper.presetCustomRequest(api, data.token);

        const res = await api.patch('/auth-ux/send-password-reset-email', data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setProcessing(true);
            variables.setError(null);
        },
        onSuccess: (_, variables) => {
            variables.setSuccess(true);
        },
        onError: (error: any, variables) => {
            variables.setError(error?.response?.data?.message ?? 'Something went wrong.');
        },
        onSettled: (_, __, variables) => {
            variables.setProcessing(false);
        },
    });
};

export const useResetPassword = () => {
    const mutationFn = async (data: HMIResetPassword): Promise<string> => {
        AuthHelper.presetCustomRequest(api, data.token);

        const res = await api.patch('/auth-ux/reset-password', data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setProcessing(true);
            variables.setError(null);
        },
        onSuccess: (_, variables) => {
            variables.setSuccess(true);
        },
        onError: (error: any, variables) => {
            variables.setError(error?.response?.data?.message ?? 'Something went wrong.');
        },
        onSettled: (_, __, variables) => {
            variables.setProcessing(false);
        },
    });
};

export const useSignIn = () => {
    const dispatch = useDispatch();

    const mutationFn = async (data: HMISignIn): Promise<ISignInResponse> => {
        AuthHelper.presetCustomRequest(api, data.token);

        const res = await api.post('/auth-ux/sign-in', data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setProcessing(true);
            variables.setError(null);
        },
        onSuccess: (data) => {
            CookiesHelper.set(AppConfig.cookieTokenNames.auth, data.auth_token, (4 * 24 * 60 * 60 * 1000));
            CookiesHelper.remove(AppConfig.cookieTokenNames.guest);
            CookiesHelper.remove(AppConfig.cookieTokenNames.session);
            dispatch(setUserData({ email: data.email, username: data.username }));

            window.location.href = '/';
        },
        onError: (error: any, variables) => {
            const errorMsg = error?.response?.data?.message ?? 'Something went wrong.';

            variables.setError(errorMsg);
            if (errorMsg === 'User email is not verified.') {
                variables.setShowResendVerification(true);
            };
        },
        onSettled: (_, __, variables) => {
            variables.setProcessing(false);
        },
    });
};

export const useSignout = () => {
    AuthHelper.presetPrivateRequest(api);

    const dispatch = useDispatch();

    const mutationFn = async (): Promise<void> => {
        const res = await api.patch('/auth-ux/sign-out');
        return res.data;
    };

    return useMutation({
        mutationFn,
        onSettled: () => {
            CookiesHelper.remove(AppConfig.cookieTokenNames.session);
            CookiesHelper.remove(AppConfig.cookieTokenNames.guest);
            CookiesHelper.remove(AppConfig.cookieTokenNames.auth);
            CookiesHelper.remove(AppConfig.cookieTokenNames.mbtsConsent);
            dispatch(setUserData(null));

            window.location.href = '/auth/sign-in';
        },
    });
};

export const useSetPassword = () => {
    const mutationFn = async (data: HMISetPassword): Promise<string> => {
        AuthHelper.presetCustomRequest(api, data.token);

        const res = await api.patch('/auth-ux/set-password', data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setProcessing(true);
            variables.setError(null);
        },
        onSuccess: (_, variables) => {
            variables.setSuccess(true);
        },
        onError: (error: any, variables) => {
            variables.setError(error?.response?.data?.message ?? 'Something went wrong.');
        },
        onSettled: (_, __, variables) => {
            variables.setProcessing(false);
        },
    });
};